import type { Operaciones } from '../../../api/operaciones';
import type { ImportacionCatalogo, LineaImportacionCatalogo } from '../../../api/ops/servicios';

/** Filtros de la vista previa de 9.5: Servicios · Profesionales (se eligen) · Ya existen · Con errores. */
export type FiltroImportar = 'servicios' | 'profesionales' | 'existen' | 'errores';

export const FILTROS_IMPORTAR: readonly { clave: FiltroImportar; texto: string }[] = [
    { clave: 'servicios', texto: 'Servicios' },
    { clave: 'profesionales', texto: 'Profesionales' },
    { clave: 'existen', texto: 'Ya existen' },
    { clave: 'errores', texto: 'Con errores' },
];

/** Textos de 9.5, tal cual el diseño. */
export const TEXTOS_IMPORTAR = {
    plantillaTitulo: 'Usa la plantilla de Reservaria',
    plantillaTexto: 'Una línea por servicio (con su duración, su precio y sus variantes) y por profesional (con los servicios que hace).',
    elegirAyuda: 'Toca para elegirlo o arrástralo aquí. Hasta 2 MB y 2.000 líneas.',
    elegirAyudaMovil: 'Toca para elegirlo en tu móvil o en Archivos. Hasta 2 MB.',
    soloArchivo: 'Solo se sube un archivo: Reservaria no se conecta a tu programa anterior ni pide sus claves.',
    noCsv: 'Tiene que ser un archivo .csv. Guarda tu hoja de cálculo como CSV.',
    grande: 'Pesa más de 2 MB. Divide el archivo en dos.',
    faltaArchivo: 'Elige un archivo CSV para seguir.',
    comprobandoTexto: 'Lo comprobamos con las mismas reglas que el alta a mano y buscamos lo que ya tienes para no duplicarlo. No se importa nada todavía.',
    importandoTexto: 'Se importa todo a la vez: si algo falla, no se queda a medias.',
    noValidoTexto: 'Revisa que la primera línea tenga los nombres de las columnas de la plantilla. No se ha importado nada.',
    // Sin texto del diseño para la vista previa caducada (24 h) o ya aplicada: se dice qué hacer.
    caducada: 'Esta vista previa ya no vale (caduca a las 24 horas). Sube el archivo otra vez: lo ya importado no se duplica.',
} as const;

/** Las líneas de un filtro: las que se crean de cada tipo, o las que ya existen. */
export function lineasDe(i: ImportacionCatalogo, filtro: Exclude<FiltroImportar, 'errores'>): LineaImportacionCatalogo[] {
    if (filtro === 'existen') {
        return i.preview.filter((l) => l.action === 'skip_existing');
    }
    return i.preview.filter((l) => (l.kind === 'service') === (filtro === 'servicios') && l.action === 'create');
}

/** Por defecto se eligen todas las líneas que se crean. */
export function elegidasPorDefecto(i: ImportacionCatalogo): number[] {
    return i.preview.filter((l) => l.action === 'create').map((l) => l.line);
}

export function resumenImportacion(i: ImportacionCatalogo, elegidas: readonly number[]) {
    const crear = i.preview.filter((l) => l.action === 'create');
    return {
        servicios: crear.filter((l) => l.kind === 'service').length,
        profesionales: crear.filter((l) => l.kind !== 'service').length,
        serviciosElegidos: crear.filter((l) => l.kind === 'service' && elegidas.includes(l.line)).length,
        profesionalesElegidos: crear.filter((l) => l.kind !== 'service' && elegidas.includes(l.line)).length,
        existen: i.preview.filter((l) => l.action === 'skip_existing').length,
        errores: i.errors.length,
    };
}

/** «Descargar plantilla» (getCatalogImportTemplate): el CSV como archivo `plantilla-catalogo-reservaria.csv`. */
export function descargarPlantillaCatalogo(ops: Operaciones, negocio: string): Promise<void> {
    return ops.getCatalogImportTemplate(negocio).then((csv) => {
        if (typeof URL.createObjectURL !== 'function') {
            return;
        }
        const enlace = document.createElement('a');
        enlace.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        enlace.download = 'plantilla-catalogo-reservaria.csv';
        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();
        URL.revokeObjectURL(enlace.href);
    });
}
